import React, { useRef, useEffect, FC, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { ReactComponent as BiometricsIcon } from "Assets/iconBiometrics.svg";
import { useBiometricsModal } from "providers/biometrics-modal";
import { useInViews } from "utility/useInViews";

import { LEARNER_SESSION } from "utility/graphQl/query";

import { usePage } from "providers/page";
import { Button } from "components/ui";
import { useGroupSessionActions } from "apps/user/learner/hooks/use-group-session-actions";
import {
  PageLoading,
  PageError,
} from "apps/user/learner/components/page-container";
import { ReactComponent as CrossIcon } from "Assets/icon/cross-thick.svg";

import useQueryParams from "components/commons/hooks/useQueryParams";
import appInfo from "utility/app-info";
import { LearnerGroupSession } from "apps/user/learner/api/types";
import { logError } from "utility/Function";
import { trackEvent } from "utility/mixpanel";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./session-detail.module.scss";
import contentStyles from "apps/user/learner/components/content/styles.module.scss";
import BiometricsCard from "apps/user/learner/components/cards/BiometricsCard";
import {
  CategoryPill,
  ContentInfoSection,
  DateInfoDisplay,
} from "apps/user/learner/components/content";
import PractitionerCard from "apps/user/learner/components/practitioner-card";

interface SessionActionButtonProps {
  actions: Record<string, { loading?: boolean; action: Function }>;
  onClickAction: (event: React.MouseEvent, action: string) => void;
  buttonState: {
    isSessionCompleted: boolean;
    isSessionOngoing: boolean;
    isRegistered: boolean;
  };
}

const SessionActionButton: FC<SessionActionButtonProps> = ({
  actions,
  onClickAction,
  buttonState,
}) => {
  const { t } = useTranslation("sessions");

  if (buttonState.isSessionOngoing) {
    return (
      <Button
        variant="enclosed-primary"
        className={styles["session-info-button"]}
        loading={actions["JOIN"]?.loading}
        onClick={(event) => onClickAction(event, "JOIN")}
        disabled={!buttonState.isSessionOngoing}
      >
        {t("sessions:detail.join")}
      </Button>
    );
  }
  const getButtonConfig = () => {
    if (buttonState.isRegistered) {
      return {
        text: t("sessions:detail.cancelSpot"),
        action: "UNREGISTER",
        loading: actions["UNREGISTER"]?.loading || false,
        variant: "cancel" as const,
      };
    }

    if (!buttonState.isRegistered) {
      return {
        text: t("sessions:detail.reserveSpot"),
        action: "REGISTER",
        loading: actions["REGISTER"]?.loading || false,
        variant: "register" as const,
      };
    }

    return null;
  };

  const config = getButtonConfig();

  if (!config) return null;

  const buttonClassName =
    config.variant === "cancel"
      ? `${styles["session-info-button"]} ${styles["session-info-button-cancel"]}`
      : styles["session-info-button"];

  const handleButtonClick = (event: React.MouseEvent) => {
    onClickAction(event, config.action);
  };

  return (
    <Button
      variant="enclosed-primary"
      className={buttonClassName}
      loading={config.loading}
      onClick={handleButtonClick}
      monochromeColour={config.variant === "cancel" ? "white" : "black"}
      icon={
        config.variant === "cancel" && (
          <CrossIcon className={styles["cancel-button-icon"]} />
        )
      }
    >
      {config.text}
    </Button>
  );
};

interface SessionDetailProps {
  sessionId: string;
}

const SessionDetail: FC<SessionDetailProps> = ({ sessionId }) => {
  const { t } = useTranslation(["sessions", "feedback"]);
  const location = useLocation();
  const pageContext = usePage();
  const onPrefixPageTitle = pageContext?.onPrefixPageTitle;
  const query = useQueryParams();
  const { ref, inView } = useInViews();

  const isViewSentRef = useRef(false);
  const { onOpen } = useBiometricsModal();

  const listName = location?.state?.listName;
  const listPosition = location?.state?.listPosition;
  const utm_medium = query.get("utm_medium");
  const utm_campaign = query.get("utm_campaign");
  const utm_term = query.get("utm_term");
  const utm_content = query.get("utm_content");
  const utm_source = query.get("utm_source");
  let utmParameters = {
    utmMedium: utm_medium,
    utmCampaign: utm_campaign,
    utmTerm: utm_term,
    utmContent: utm_content,
    utmSource: utm_source,
  };

  const { loading, error, data } = useQuery(LEARNER_SESSION, {
    onError(error) {
      logError("Error while fetching the session", error);
    },
    variables: {
      id: sessionId,
      appInfo,
    },
    pollInterval: 60 * 1000,
  });

  const actions = useGroupSessionActions({
    session: data?.learnerSession,
    utmParameters: utmParameters,
  });

  const handleClickActionButton = (event, action) => {
    event.preventDefault();

    actions[action].action(listName, listPosition);
  };

  const trackSessionView = useCallback(() => {
    if (!data?.learnerSession || isViewSentRef.current) return;

    const { __typename, id, title } =
      data.learnerSession as LearnerGroupSession;

    if (__typename === "LearnerGroupSession") {
      trackEvent("Live Session Viewed", {
        "Session ID": id,
        "Session Title": title,
        "List Name": listName,
        "List Position": listPosition,
      });
    } else if (__typename === "LearnerPrivateSession") {
      trackEvent("Private Session Viewed", {
        "Session ID": id,
        "Session Title": title,
        "List Name": listName,
        "List Position": listPosition,
      });
    }

    isViewSentRef.current = true;
  }, [data?.learnerSession, listName, listPosition]);

  useEffect(() => {
    if (inView && data?.learnerSession) {
      trackSessionView();
    }
  }, [inView, data?.learnerSession, trackSessionView]);

  useEffect(() => {
    if (onPrefixPageTitle && data?.learnerSession?.title) {
      onPrefixPageTitle(data?.learnerSession?.title);
    }
  }, [data?.learnerSession?.title, onPrefixPageTitle]);

  if (loading) {
    return (
      <div className={contentStyles["modal-error-loading-screen"]}>
        <PageLoading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={contentStyles["modal-error-loading-screen"]}>
        <PageError />
      </div>
    );
  }

  const {
    about,
    date,
    imageUrl,
    practitioner,
    topic,
    time,
    title,
    buttonState,
    duration,
  } = data.learnerSession as LearnerGroupSession;

  return (
    <div className={styles["session-container"]} ref={ref}>
      <div className={styles["session-image-placeholder"]}>
        <img
          className={styles["session-picture"]}
          src={imageUrl}
          alt="Session Pic"
        />
        <div className={styles["image-overlay"]}>
          <div className={styles["session-info-headings"]}>
            <div className={styles["image-overlay-duration-container"]}>
              <p className={styles["image-overlay-duration"]}>{duration}</p>
              {topic && <CategoryPill text={topic} />}
            </div>
            <p className={styles["image-overlay-title"]}>{title}</p>
            <p className={styles["image-overlay-practitioner"]}>
              {t("feedback:withPractitioner", {
                name: practitioner.fullName,
              })}
            </p>
          </div>
        </div>
      </div>
      <div className={styles["content-layout"]}>
        <div className={styles["session-info-header"]}>
          <div className={styles["session-info-date"]}>
            <DateInfoDisplay
              date={date}
              time={time}
              isCompleted={buttonState.isSessionCompleted}
            />
            {buttonState.isSessionCompleted && (
              <div className={styles["session-completed-message"]}>
                {t("sessions:detail.finished")}
              </div>
            )}
          </div>
          <div className={styles["session-action-button"]}>
            {!buttonState.isSessionCompleted && (
              <SessionActionButton
                actions={actions}
                onClickAction={handleClickActionButton}
                buttonState={buttonState}
              />
            )}
            {buttonState.isSessionOngoing && (
              <div className={styles["session-ongoing-container"]}>
                <div className={styles["session-ongoing-icon"]}>
                  <div className={styles["session-ongoing-dot"]} />
                  <span className={styles["session-ongoing-text"]}>
                    {t("sessions:detail.liveBadge")}
                  </span>
                </div>
                <span className={styles["session-ongoing-message"]}>
                  {t("sessions:detail.begun")}
                </span>
              </div>
            )}
          </div>
        </div>
        <ContentInfoSection
          title={t("sessions:detail.inThisSession")}
          content={about}
        />
        {buttonState.shouldEnableTrackBiometrics && (
          <div className={styles["session-info-biometrics"]}>
            <BiometricsIcon className={styles["biometrics-icon"]} />
            <span className={styles["session-info-biometrics-text"]}>
              {t("sessions:detail.worksWithBiometrics")}
            </span>
          </div>
        )}
        <PractitionerCard practitioner={practitioner} />
      </div>
      {buttonState.shouldEnableTrackBiometrics && (
        <div className={styles["session-info-biometrics-card"]}>
          <BiometricsCard onOpen={onOpen} />
        </div>
      )}
    </div>
  );
};

export default SessionDetail;
